import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Button,
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import SongList from "./SongList";
import SongModal from "./SongModal";
import { songService } from "./songService";
import { enqueueSnackbar } from "notistack";
import Header from "../Header";

const SongsMaster = () => {
  const [songs, setSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedSongId, setSelectedSongId] = useState(null);
  
  // Lazy loading state
  const [visibleCount, setVisibleCount] = useState(50); // Number of songs to show initially
  const itemsPerLoad = 50; // Number of songs to load per scroll
  const scrollContainerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const songsData = await songService.fetchSongs();
      setSongs(songsData);
    } catch (err) {
      console.error("Failed to fetch data", err);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSelectedSongId(null);
    setEditingIndex(null);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingIndex(null);
    setSelectedSongId(null);
  };

  const handleEdit = async (songId) => {
    const index = songs.findIndex((song) => song.songs_master_id === songId);
    if (index === -1) return;
    setEditingIndex(index);
    setSelectedSongId(songId);
    setOpen(true);
  };

  const handleDelete = async (songId) => {
    try {
      await songService.deleteSong(songId);
      await fetchAllData();
    } catch (err) {
      console.error("Delete song failed", err);
    }
  };

  const handleSave = async (songData, selectedPlaylists, selectedGenres, tempUrls, tempFiles) => {
    try {
      const existingDuplicate = songs.find(
        (song) =>
          song.name.trim().toLowerCase() === songData.name.trim().toLowerCase() &&
          song.spotify_id === songData.spotify_id &&
          (editingIndex === null ||
            song.songs_master_id !== songs[editingIndex].songs_master_id)
      );

      if (existingDuplicate) {
        enqueueSnackbar("A song with this name and Spotify ID already exists.", {
          variant: "warning",
          autoHideDuration: 3000,
        });
        return false;
      }

      const payload = {
        name: songData.name,
        artist: songData.artist,
        album: songData.album,
        decade: songData.decade,
        duration: songData.duration,
        image: songData.image,
        release_year: songData.release_year,
        spotify_id: songData.spotify_id, // Add spotify_id to payload
      };

      let songId;

      if (editingIndex !== null) {
        songId = songs[editingIndex].songs_master_id;
        await songService.updateSong(songId, payload);
        await songService.removeSongMappings(songId);
      } else {
        songId = await songService.createSong(payload);
      }

      // Add URLs, files, playlists, and genres
      await Promise.all([
        songService.addSongUrls(songId, tempUrls),
        songService.addSongFiles(songId, tempFiles),
        songService.addSongPlaylists(songId, selectedPlaylists),
        songService.addSongGenres(songId, selectedGenres),
      ]);

      await fetchAllData();
      handleClose();
      return true;
    } catch (err) {
      console.error("Error saving song:", err);
      enqueueSnackbar("Failed to save song. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
      return false;
    }
  };

  // Memoize filtered songs to prevent unnecessary recalculations
  const filteredSongs = useMemo(() => {
    return songs.filter(
      (song) =>
        song.name && song.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [songs, searchTerm]);

  // Lazy loading logic - show only visible songs
  const visibleSongs = useMemo(() => {
    return filteredSongs.slice(0, visibleCount);
  }, [filteredSongs, visibleCount]);

  // Reset visible count when search term changes
  useEffect(() => {
    setVisibleCount(50);
  }, [searchTerm]);

  // Load more songs when scrolling
  const loadMoreSongs = useCallback(() => {
    if (isLoading || visibleCount >= filteredSongs.length) return;
    
    setIsLoading(true);
    // Simulate slight delay for smooth loading
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + itemsPerLoad, filteredSongs.length));
      setIsLoading(false);
    }, 300);
  }, [isLoading, visibleCount, filteredSongs.length, itemsPerLoad]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!loadMoreTriggerRef.current || visibleCount >= filteredSongs.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          loadMoreSongs();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    const currentTrigger = loadMoreTriggerRef.current;
    observer.observe(currentTrigger);

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [loadMoreSongs, visibleCount, filteredSongs.length]);

  return (
    <>
      <Header />
      <Container
        sx={{
          mt: 2,
          pb: 4,
          bgcolor: "#121212",
          minHeight: "90vh",
          border: "1px solid",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            pb: 2,
            borderBottom: "1px solid",
          }}
        >
          <Typography variant="h4">Songs</Typography>
          <Button variant="contained" color="success" onClick={handleOpen}>
            Add Song
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 2, flexWrap: "wrap", gap: 2 }}>
          <TextField
            fullWidth
            label="Search Songs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: "200px" }}
          />
          <Typography variant="body2" sx={{ color: "#999", whiteSpace: "nowrap" }}>
            Showing {visibleSongs.length} of {filteredSongs.length} songs
          </Typography>
        </Box>

        <Box 
          ref={scrollContainerRef} 
          sx={{ 
            maxHeight: "calc(100vh - 300px)", 
            overflowY: "auto",
            overflowX: "hidden"
          }}
        >
          <SongList
            songs={visibleSongs}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          
          {/* Trigger element for intersection observer */}
          {visibleCount < filteredSongs.length && (
            <Box 
              ref={loadMoreTriggerRef}
              sx={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                py: 3,
                minHeight: "50px"
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: "#1DB954" }} />
              ) : (
                <Typography variant="body2" sx={{ color: "#999" }}>
                  Scroll for more...
                </Typography>
              )}
            </Box>
          )}
          
          {/* End of list indicator */}
          {visibleCount >= filteredSongs.length && filteredSongs.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 3 }}>
              <Typography variant="body2" sx={{ color: "#999" }}>
                All songs loaded
              </Typography>
            </Box>
          )}
        </Box>

        <SongModal
          open={open}
          onClose={handleClose}
          onSave={handleSave}
          editingIndex={editingIndex}
          selectedSongId={selectedSongId}
          songs={songs}
        />
      </Container>
    </>
  );
};

export default SongsMaster;